// `config/` sits outside `src/` and has no alias, like root-level `lib/`.
import {
  getProBuyerSellersPath as getProBuyerSellersPathForLocale,
  getProBuyerSellersPublicPath,
} from "../../../../config/pro-buyer-paths";
import { ACCOUNT_ADS_ROUTE } from "@/common/static/routes.static";
import { CANONICAL_HOST } from "@/config";
import { locales } from "@/i18n/config";

/**
 * WHO-5654. Shop-wide Pro Buyer discount for business sellers: the seller side.
 *
 * Naming (Evelien, 17 Sep 2026): customer-facing copy says "Pro Buyer" in
 * every locale, untranslated and capitalised. Never bare "Pro", never
 * "trade". Internal names (code, gateway fields) keep "trade".
 */

/** The only percentages the gateway accepts; the monolith enforces the same ladder. */
export const TRADE_DISCOUNT_STEPS = [10, 15, 20, 25, 30, 35, 40] as const;

export type TradeDiscountStep = (typeof TRADE_DISCOUNT_STEPS)[number];

/** Preselected on the first expand, marked "Aanbevolen". */
export const TRADE_DISCOUNT_DEFAULT_PCT: TradeDiscountStep = 15;

/**
 * The explanation page for sellers, localized per locale (Thomas, 25 Sep):
 * nl /pro-buyer/verkopers, de /pro-buyer/verkaeufer, fr /pro-buyer/vendeurs,
 * it /pro-buyer/venditori, es /pro-buyer/vendedores, en /pro-buyer/sellers.
 * Returned without the locale prefix: `Link` adds it. The table lives in
 * `config/pro-buyer-paths.js` because the rewrites, redirects and sitemap
 * need it too. Every in-app link to the page goes through this helper.
 */
export const getProBuyerSellersPath = (locale: string | undefined): string =>
  getProBuyerSellersPathForLocale(locale);

/**
 * The hreflang cluster for `HrefLang urlMap`: every routed locale's path
 * without prefix (`/en-us` gets the English word, WHO-5766). Passed as `hreflangUrls` page prop, which also stops `_app` from
 * rendering the asPath-based tags: the page is ISR, so its prerendered
 * asPath is the internal `/pro-buyer/sellers`, not the localized URL.
 */
export const getProBuyerSellersHreflangUrls = (): Record<string, string> =>
  Object.fromEntries(
    locales.map(locale => [locale, getProBuyerSellersPathForLocale(locale)])
  );

/** Absolute canonical URL of the page in a locale. */
export const getProBuyerSellersCanonicalUrl = (
  locale: string | undefined
): string => `${CANONICAL_HOST}${getProBuyerSellersPublicPath(locale)}`;

/**
 * `/account/ads?pro-buyer=setup`: the dashboard action and the explanation
 * page deep-link here with the block already expanded.
 */
export const PRO_BUYER_SETUP_QUERY_KEY = "pro-buyer";
export const PRO_BUYER_SETUP_QUERY_VALUE = "setup";
export const PRO_BUYER_SETUP_ROUTE = `${ACCOUNT_ADS_ROUTE}?${PRO_BUYER_SETUP_QUERY_KEY}=${PRO_BUYER_SETUP_QUERY_VALUE}`;

/** Dashboard nudge: hidden for 30 days after the first "Verberg", shown once more, then never. */
export const DASHBOARD_DISMISS_DAYS = 30;
export const DASHBOARD_DISMISS_MAX = 2;
export const DASHBOARD_DISMISS_STORAGE_PREFIX =
  "whoppah.proBuyerDiscount.dashboardDismiss.";

/** Help center category holding the Pro Buyer FAQs; same slug in all six locales. */
export const PRO_BUYER_HELP_CATEGORY_SLUG = "pro-buyer";

/**
 * Revalidate window for /pro-buyer/sellers when help was unavailable or the
 * category was empty: short, so the FAQ section comes back soon after the
 * help-service recovers. A healthy fetch uses HELP_REVALIDATE_SECONDS.
 */
export const PRO_BUYER_FAQ_RETRY_REVALIDATE_SECONDS = 60;
